import {
  Options,
  REPORT_TYPE,
  PageHistory,
  EventDataType,
  CMD,
  BridgeTypeEnum,
  MetricConfig,
  ReportCustomMetricParams,
} from "./index.interface"
import type { MetricNameOf } from "./index.interface"
import { addEvent, hookRouterStateChange, getEventCommonParams } from "./util/common"
import { uid } from "./util/uid"
import { createPerfWatcher } from "./services/perf"
import { report } from "./services/report"
import STORE from "./util/storage"
import { createApi, type HttpRequest } from "./util/api"
import { createViewAndVisit } from "./services/viewAndVisit"
import CustomBrigde from "./util/bridge"
import { REPORT_TIME_INTERVAL, MAX_EVENTLIST_LENGTH } from "./util/constants"

/**
 * @credit/insight-sdk
 *
 * Credit 前端性能监控 SDK，用于采集页面性能指标、API 请求、用户行为等数据并上报至 Insight 平台。
 *
 * 多实例模式
 *
 * @example
 * ```typescript
 * import { Tracer } from '@credit/insight-sdk'
 *
 * const tracer = new Tracer().init({
 *   region: 'id',       // 国家/地区，如 id / th / ph
 *   bizType: 'spl',     // 业务类型
 *   useWeb: true,       // true: 上报至 web 地址，false: 上报至 origin/credit/insight/fe_chat
 *   isLive: false,      // true: 生产环境，false: 测试环境
 * })
 *
 * // 上报自定义事件
 * tracer.reportCustomInfo('user_action', { step: 'checkout' })
 *
 * // 上报点击事件
 * tracer.reportClickEvent('btn-submit')
 *
 * // 上报错误信息
 * tracer.reportErrorInfo('payment failed', { orderId: '12345' })
 * ```
 *
 * 单实例模式
 * - 单实例模式下，所有方法都通过 Insight 对象调用，如果存在 sdk 内部依赖的方式，会导致 sdk 内部无法自定义上报逻辑
 * - 如果是 sdk 模式，建议使用多实例模式
 *
 * @example
 * ```typescript
 * // 使用默认实例
 * import Insight from '@credit/insight-sdk'
 *
 * Insight.init({
 *   region: 'id',
 *   bizType: 'spl',
 *   useWeb: true,
 * })
 *
 * // 上报自定义事件
 * Insight.reportCustomInfo('user_action', { step: 'checkout' })
 *
 * // 上报点击事件
 * Insight.reportClickEvent('btn-submit')
 *
 * // 上报错误信息
 * Insight.reportErrorInfo('payment failed', { orderId: '12345' })
 * ```
 *
 * @module @credit/insight-sdk
 */
export class Tracer<TMetricName extends string = string> {
  // ─── 私有字段 ────────────────────────────────────────────────────────────────

  private _options: Options = {
    appId: "",
    region: "",
    bizType: "",
    isLive: true,
    url: "",
    autoReportView: true,
    autoReportApi: true,
    autoReportPerf: true,
    autoReportVisit: true,
    autoReportBridge: true,
    manualOnly: false,
    sampleRate: 1,
    maxEventListLength: MAX_EVENTLIST_LENGTH,
    ignore: {
      ignoreApis: [],
    },
    whiteListApis: [],
    useFMP: true,
    useFCP: true,
    useTTI: true,
    useLCP: true,
    useINP: true,
    useWeb: false,
    useFetch: true,
    sessionId: uid(),
  }

  private _eventList: EventDataType[] = []
  private _bridgeList: any = {}
  private _pageHistory: PageHistory[] = []
  private _fromUrl: string = ""

  // 实例独立的定时器句柄
  private _checker: ReturnType<typeof setInterval> | undefined

  // API Hook 实例引用，用于 destroy 时清理
  private _apiInstance: HttpRequest | undefined

  // 实例独立的 localStorage key（由 bizType + region 组成，init 后确定）
  private _eventListKey: string = "TRACER_EVENTLIST"
  private _pageHistoryKey: string = "TRACER_PAGEHISTORY"
  private _bridgeListKey: string = "TRACER_BRIDGELIST"

  // ─── 私有方法：初始化 ─────────────────────────────────────────────────────────

  /**
   * 初始化实例独立的 storage key
   *
   * 根据 bizType 和 region 生成实例专属的 storage key
   */
  private initStorageKeys(): void {
    const { bizType, region } = this._options
    const suffix = `${bizType.toUpperCase()}_${region.toUpperCase()}`

    this._eventListKey = `TRACER_EVENTLIST_${suffix}`
    this._pageHistoryKey = `TRACER_PAGEHISTORY_${suffix}`
    this._bridgeListKey = `TRACER_BRIDGELIST_${suffix}`
  }

  /**
   * 初始化上报 URL
   *
   * - 如果 useWeb 为 true，使用 web 上报地址（根据 isLive 区分生产/测试环境）
   * - 如果 useWeb 为 false 且未指定 url，使用默认的 credit/insight/fe_chat 路径
   */
  private initUrl(): void {
    const { useWeb, isLive } = this._options
    if (useWeb) {
      this._options.url = isLive
        ? "https://insight.shopeekredit.co.id/api/report"
        : "https://insight.test.shopeekredit.co.id/api/report"
      return
    }

    if (!this._options.url) {
      this._options.url = `${window.location.origin}/credit/insight/fe_chat`
    }
  }

  /**
   * 挂载所有事件监听器和 Hook，由两类组成：
   *
   * 1. **Auto collectors**（受 `autoReport*` flag 控制）—— 关闭后完全不安装对应
   *    全局 Hook，避免对宿主的 fetch / pushState / bridge 等全局产生副作用。
   * 2. **Flush handlers** —— `beforeunload` 时把队列里的数据发出去。`_checker`
   *    清理和 `checkEventList` flush 始终安装（`reportCustomInfo` 等手动通道
   *    也依赖它们）；`reportPageHistory` flush 只在追踪页面时安装。
   */
  private initListeners(): void {
    this.installAutoCollectors()
    this.installFlushHandlers()
  }

  /** 是否需要追踪页面级生命周期（PV / Visit / Stay） */
  private shouldTrackPages(): boolean {
    return !!(this._options.autoReportView || this._options.autoReportVisit)
  }

  private installAutoCollectors(): void {
    if (this._options.autoReportPerf) this.installPerfCollector()
    if (this.shouldTrackPages()) this.installRouteCollector()
    if (this._options.autoReportApi) this.installApiCollector()
    if (this._options.autoReportBridge) this.installBridgeCollector()
  }

  private installFlushHandlers(): void {
    addEvent(window, "beforeunload", () => clearInterval(this._checker))
    addEvent(window, "beforeunload", () => this.checkEventList())
    if (this.shouldTrackPages()) {
      addEvent(window, "beforeunload", () => this.reportPageHistory())
    }
  }

  private installPerfCollector(): void {
    const { reportPerf, reportCLS } = createPerfWatcher(this._options)
    addEvent(window, "load", () => {
      console.info("[Insight] [Core] [initListeners] start to report perf and cls")
      reportPerf(this.recordData.bind(this))
      reportCLS(this.recordData.bind(this))
    })
  }

  private installRouteCollector(): void {
    hookRouterStateChange()
    createViewAndVisit(
      this._options,
      this.recordData.bind(this),
      this.getPageHistory.bind(this),
      this.setFromUrl.bind(this),
      this.setPageHistory.bind(this)
    )
  }

  private installApiCollector(): void {
    this._apiInstance = createApi(this._options, this.recordData.bind(this))
  }

  private installBridgeCollector(): void {
    new CustomBrigde({
      updateEventList: this.updateEventList.bind(this),
      cacheBridge: this.cacheBridge.bind(this),
      deleteBridge: this.deleteBridge.bind(this),
      getCacheBridge: this.getCacheBridge.bind(this),
    })
  }

  /** 启动定时上报检查器 */
  private initChecker(): void {
    this._checker = setInterval(() => this.checkEventList(), REPORT_TIME_INTERVAL)
  }

  // ─── 私有方法：数据路由 ───────────────────────────────────────────────────────

  /**
   * 根据上报类型将数据分发到对应的队列
   */
  private recordData(recordObj: any): void {
    try {
      switch (recordObj.type) {
        case REPORT_TYPE.Visit:
          this.updatePageHistory(recordObj)
          break
        case REPORT_TYPE.View:
        case REPORT_TYPE.Click:
        case REPORT_TYPE.Api:
        case REPORT_TYPE.Perf:
        case REPORT_TYPE.Stay:
        case REPORT_TYPE.Bridge:
          this.updateEventList(recordObj)
          break
        default:
          console.warn(`[Insight] [Core] [recordData] unsupported type: ${recordObj.type}`)
          this.reportErrorInfo(`unsupported report type: ${recordObj.type}`)
          break
      }
    } catch (e) {
      console.warn("[Insight] [Core] [recordData] error:", e)
      this.reportErrorInfo(`recordData error: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  // ─── 私有方法：队列管理 ───────────────────────────────────────────────────────

  /** 检查并上报 eventList 中的待发数据 */
  private checkEventList(): void {
    if (this._eventList.length > 0) {
      report(this._options, {
        eventList: this._eventList,
        cmd: CMD.REPORT,
      })
      this.clearEventList()
    }
  }

  private updatePageHistory(recordObj: PageHistory): void {
    recordObj = {
      ...getEventCommonParams(this._fromUrl),
      ...recordObj,
    }
    this._pageHistory.push(recordObj)
    STORE.set(this._pageHistoryKey, this._pageHistory)
  }

  private clearEventList(): void {
    this._eventList = []
    STORE.remove(this._eventListKey)
  }

  private clearPageHistory(): void {
    this._pageHistory = []
    STORE.remove(this._pageHistoryKey)
  }

  private clearAll(): void {
    this.clearEventList()
    this.clearPageHistory()
  }

  // ─── 私有方法：localStorage 容灾 ─────────────────────────────────────────────

  /** 读取 localStorage 中的缓存数据并上报 */
  private checkStorage(): void {
    let eventList: any = STORE.get(this._eventListKey)
    const pageHistory: any = STORE.get(this._pageHistoryKey)
    const bridgeList: any = STORE.get(this._bridgeListKey)
    if (bridgeList) {
      if (!eventList) eventList = []
      Object.keys(bridgeList).forEach((item) => {
        eventList.push(bridgeList[item])
      })
    }
    if (eventList || pageHistory) {
      report(this._options, {
        eventList,
        pageHistory,
        cmd: CMD.REPORT,
      })
    }
  }

  // ─── 私有方法：工具方法 ───────────────────────────────────────────────────────

  private getPageHistory(): PageHistory[] {
    return this._pageHistory
  }

  private setPageHistory(pageHistory: PageHistory[]): void {
    this._pageHistory = pageHistory
  }

  private setFromUrl(url: string): void {
    this._fromUrl = url
  }

  // ─── 公开方法：初始化 ─────────────────────────────────────────────────────────

  /**
   * 初始化 SDK
   *
   * 当传入 `metricConfigs` 时，返回值会携带具体的泛型类型，
   * 使 `reportCustomInfo` 的 `message` 参数收窄为 `metricName` 联合类型。
   *
   * @param options - 初始化配置项，详见 Options 类型定义
   * @throws {Error} 当 options 不是对象时抛出
   *
   * @example
   * ```typescript
   * const tracer = new Tracer().init({
   *   region: 'id',
   *   bizType: 'spl',
   *   metricConfigs: [
   *     { metricName: 'page_load_time', type: 'histogram', labels: [], desc: '' },
   *     { metricName: 'api_error_count', type: 'counter',  labels: [], desc: '' },
   *   ],
   * })
   *
   * // message 类型自动收窄为 'page_load_time' | 'api_error_count'
   * tracer.reportCustomInfo('page_load_time', {}, [{ metricName: 'page_load_time', value: 100 }])
   * ```
   */
  public init<T extends MetricConfig[]>(
    options: Omit<Options, "metricConfigs"> & { metricConfigs?: T }
  ): Tracer<MetricNameOf<T>> {
    console.info("[Insight] [Core] [init] init options >>>>>>", options)
    if (!options || typeof options !== "object") {
      throw new Error("options is required and must be an object")
    }

    Object.assign(this._options, options)
    this.normalizeOptions()

    this.initStorageKeys()
    this.initUrl()
    this.reportOutdatedStorage()
    this.initListeners()
    this.initChecker()

    return this as unknown as Tracer<MetricNameOf<T>>
  }

  /**
   * 在 init 主流程之前对 `_options` 做派生与归一化，把元开关展开成具体的
   * 字段。当前处理 `manualOnly` 和 `maxEventListLength` 默认值回退，未来若有
   * sampleRate 边界裁剪、bizType 补全等派生逻辑，统一进这里。
   *
   * **`manualOnly` 是硬覆盖**：开启时强制把 5 个 `autoReport*` 字段置为 false，
   * 即使用户显式传了 `autoReport*: true` 也会被覆盖。这样设计是为了让"嵌入下游
   * SDK"场景一行配置就能彻底关掉所有自动采集，语义不留歧义。需要细粒度控制
   * 的调用方应直接使用各 `autoReport*` 字段，不要传 `manualOnly`。
   */
  private normalizeOptions(): void {
    if (this._options.manualOnly) {
      this._options.autoReportApi = false
      this._options.autoReportView = false
      this._options.autoReportVisit = false
      this._options.autoReportPerf = false
      this._options.autoReportBridge = false
    }

    const { maxEventListLength } = this._options
    if (
      typeof maxEventListLength !== "number" ||
      !isFinite(maxEventListLength) ||
      maxEventListLength <= 0 ||
      maxEventListLength % 1 !== 0
    ) {
      this._options.maxEventListLength = MAX_EVENTLIST_LENGTH
    }
  }

  // ─── 公开方法：容灾上报 ───────────────────────────────────────────────────────

  /**
   * 上报 localStorage 中缓存的未发送数据
   *
   * 适用于不支持 `beforeunload` 的 WebView 环境，由 Native 侧在页面退出时主动调用。
   */
  public reportOutdatedStorage(): void {
    this.checkStorage()
    this.clearAll()
  }

  // ─── 公开方法：事件上报 ───────────────────────────────────────────────────────

  /**
   * 将事件数据写入队列，填充公共参数并处理 Prometheus 指标
   */
  public updateEventList(recordObj: EventDataType): void {
    recordObj = {
      ...getEventCommonParams(this._fromUrl),
      ...recordObj,
    }

    if (recordObj?.metricData?.length) {
      const metricConfigs: MetricConfig[] = []
      recordObj.metricData.forEach((metricData) => {
        const targetConfigArr = this._options.metricConfigs?.filter(
          (config) => config.metricName === metricData.metricName
        )
        if (targetConfigArr?.length === 1) {
          metricConfigs.push({ ...targetConfigArr[0], ...metricData })
        }
      })
      delete recordObj.metricData
      recordObj.data.metricConfigs = metricConfigs
    }

    this._eventList.push(recordObj)
    STORE.set(this._eventListKey, this._eventList)
    if (this._eventList.length >= (this._options.maxEventListLength ?? MAX_EVENTLIST_LENGTH)) {
      this.checkEventList()
    }
  }

  /**
   * 上报错误信息
   *
   * @param error - 错误描述
   * @param extra - 附加信息
   */
  public reportErrorInfo(error: string, extra?: object): void {
    this.updateEventList({
      type: REPORT_TYPE.Error,
      data: {
        error,
        extra: extra || {},
      },
    })
  }

  /**
   * 上报自定义事件，支持携带 Prometheus 指标数据
   *
   * 当 `init` 时传入了 `metricConfigs`，`message` 类型会自动收窄为
   * 所有 `metricName` 的联合类型，提供编译期校验和 IDE 自动补全。
   *
   * @param message - 事件名称（有 metricConfigs 时收窄为 metricName 联合类型）
   * @param extra - 附加信息
   * @param metricData - 自定义指标数据
   */
  public reportCustomInfo(
    message: TMetricName,
    extra?: object,
    metricData?: ReportCustomMetricParams[]
  ): void {
    this.updateEventList({
      type: REPORT_TYPE.Custom,
      data: {
        message,
        extra: extra || {},
      },
      metricData,
    })
  }

  /**
   * 上报点击事件
   *
   * @param clickId - 点击元素标识
   */
  public reportClickEvent(clickId: string): void {
    this.recordData({
      ...getEventCommonParams(this._fromUrl),
      type: REPORT_TYPE.Click,
      data: clickId,
    })
  }

  /**
   * 手动触发页面访问记录上报
   */
  public reportPageHistory(): void {
    report(this._options, {
      pageHistory: this._pageHistory,
      cmd: CMD.REPORT,
    })
    this.clearPageHistory()
  }

  // ─── 公开方法：Bridge 上报 ────────────────────────────────────────────────────

  /** 上报 Bridge 调用超时 */
  public reportBridgeOverTime(bridgeName: string): void {
    this.updateEventList({
      type: REPORT_TYPE.Bridge,
      data: {
        type: BridgeTypeEnum.custom,
        name: bridgeName,
        overTime: true,
      },
    })
  }

  /** 上报 Bridge 自定义数据 */
  public reportBridgeCustom(bridgeName: string, data: object = {}): void {
    this.updateEventList({
      type: REPORT_TYPE.Bridge,
      data: {
        ...data,
        type: BridgeTypeEnum.custom,
        name: bridgeName,
      },
    })
  }

  public cacheBridge(uuid: string, data: any): void {
    this._bridgeList[uuid] = {
      ...getEventCommonParams(this._fromUrl),
      ...data,
    }
    STORE.set(this._bridgeListKey, this._bridgeList)
  }

  public deleteBridge(uuid: string): void {
    delete this._bridgeList[uuid]
    STORE.set(this._bridgeListKey, this._bridgeList)
  }

  public getCacheBridge(uuid: string): any {
    return this._bridgeList[uuid]
  }

  // ─── 公开方法：工具方法 ───────────────────────────────────────────────────────

  /**
   * 获取当前实例的配置
   */
  public getOptions(): Options {
    return this._options
  }

  /**
   * 销毁当前实例，清理定时器和 API Hook 注册
   *
   * 适用于 SPA 中页面级实例的清理，防止内存泄漏和重复上报。
   */
  public destroy(): void {
    if (this._checker) {
      clearInterval(this._checker)
      this._checker = undefined
    }
    this._apiInstance?.destroy()
    this._apiInstance = undefined
  }
}
